import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, PlusIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useOutletContext, useParams } from 'react-router-dom'

import { useCreateMarketShopProduct } from '@/api/market-shop/inventory/create-market-shop-product'
import { useGetMarketShopInventoryItem } from '@/api/market-shop/inventory/get-market-shop-inventory-item'
import { useGetMarketShopInventoryTags } from '@/api/market-shop/inventory/get-market-shop-inventory-tags'
import { updateMarketShopProduct } from '@/api/market-shop/inventory/update-market-shop-product'
import { useUploadMarketShopProductImage } from '@/api/market-shop/inventory/upload-market-shop-product-image'
import { useGetMarketShopLink } from '@/api/market-shop/settings/get-market-shop-link'
import { useGetMarketShopSettings } from '@/api/market-shop/settings/get-market-shop-settings'
import { FileUploadFormField, NumericInputFormField, SelectFormField } from '@/components/form'
import { TextAreaFormField } from '@/components/form/text-area-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import {
  IMAGE_UPLOAD_LIMITS,
  PHOTO_IMAGE_OPTIMIZATION,
  useImageUploadErrorToast,
} from '@/components/ui/file-upload'
import { Icon } from '@/components/ui/icon'
import { SelectOption } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { routerFactory, toastFactory } from '@/factories'
import { useFormSave, useNavigationGuard } from '@/hooks'
import { getMarketShopImageHref } from '@/pages/market-shop/market-shop.utils'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { getApiErrorMessage, transformCentsToDollars, transformDollarsToCents } from '@/utils'

import {
  createMarketShopProductSchema,
  DEFAULT_MARKET_SHOP_PRODUCT_FORM_DATA,
  ITEM_DESCRIPTION_MAX_LENGTH,
  ITEM_NAME_MAX_LENGTH,
  MarketShopProductFormData,
  PRODUCT_IMAGE_ACCEPTED_TYPES,
} from './market-shop-product.constants'
import * as S from './market-shop-product.styled'
import { MarketShopProductSkeleton } from './market-shop-product-skeleton'

export const MarketShopProductPage = () => {
  const { t } = useTranslation(['market-shop-dashboard', 'common'])
  const handleImageValidationError = useImageUploadErrorToast({
    acceptedTypes: PRODUCT_IMAGE_ACCEPTED_TYPES,
    ...IMAGE_UPLOAD_LIMITS,
  })
  const { id: rawId } = useParams<{ id?: string }>()
  const itemId = rawId ? Number(rawId) : undefined
  const isEditMode = !!itemId

  const { navigateBack } = routerFactory.useRouter()
  const activeBuilding = useSelector(selectActiveBuilding)
  const buildingId = activeBuilding?.id

  const { data: marketShopLinks } = useGetMarketShopLink({ buildingId })
  const marketShopId = marketShopLinks?.[0]?.id
  const { data: product, isLoading } = useGetMarketShopInventoryItem(itemId)
  const { data: tags = [] } = useGetMarketShopInventoryTags({ buildingId: buildingId ?? 0 })
  const { data: settings } = useGetMarketShopSettings({ marketShopId: marketShopId ?? 0 })
  const { mutateAsync: createMarketShopProduct } = useCreateMarketShopProduct()
  const { mutateAsync: uploadProductImage } = useUploadMarketShopProductImage()

  const discountTag = tags?.find((tag) => tag.name === 'Discount')

  const marketShopProductSchema = useMemo(
    () => createMarketShopProductSchema(t, discountTag?.id),
    [t, discountTag?.id],
  )

  const form = useForm<MarketShopProductFormData>({
    defaultValues: {
      ...DEFAULT_MARKET_SHOP_PRODUCT_FORM_DATA,
      tax: settings?.globalTax?.toString() ?? '',
    },
    mode: 'onChange',
    resolver: zodResolver(marketShopProductSchema),
    values: product
      ? {
          buildingId: buildingId ?? 0,
          // Note: If there is no category, it means the product is displayed in the "All" category
          categoryId: product.categoryId?.id ?? 'all',
          description: product.description ?? '',
          discount: product.tags.find((tag) => tag.name === 'Discount')?.discount?.toString() ?? '',
          image: product.image ? [getMarketShopImageHref(product.image)] : [],
          price: transformCentsToDollars(product.price).toString(),
          quantity: product.quantity.toString(),
          summary: product.summary,
          tags: product.tags.map((tag) => tag.id),
          tax: product.tax?.toString() ?? settings?.globalTax?.toString() ?? '',
          unlimited: product.unlimited,
        }
      : undefined,
  })
  const { watch, formState, setValue, trigger } = form
  const { isDirty, isSubmitting, isSubmitted } = formState
  const { isDeleting } = useOutletContext<{ isDeleting: boolean }>()

  const formValues = watch()
  const isDiscountTagAdded = !!discountTag && formValues.tags.includes(discountTag.id)

  const navigateToInventory = () => {
    navigateBack({ path: '/dashboard/building-dashboard/market-shop/inventory' })
  }

  useNavigationGuard({
    isBlocked: isDirty && !isSubmitting && !isDeleting,
    message: t('market-shop-dashboard:product.unsavedChangesMessage'),
  })

  const handleCancel = () => navigateToInventory()

  const handleSubmit = async (values: MarketShopProductFormData) => {
    if (!buildingId) return

    try {
      const imageFilename = values.image[0]?.split('/').pop() ?? null

      const payload = {
        ...values,
        buildingId: buildingId ?? 0,
        // Note: If category is 'all' - it means that the item shouldn't have a category
        categoryId: values.categoryId === 'all' ? null : Number(values.categoryId),
        image: imageFilename,
        price: transformDollarsToCents(values.price),
        quantity: Number(values.quantity),
        tags: values.tags.map((tagId) => ({
          tagId,
          ...(discountTag?.id === tagId ? { discount: Number(values.discount) } : {}),
        })),
        tax: Number(values.tax),
      }

      if (!isEditMode) {
        await createMarketShopProduct(payload)

        toastFactory.success(
          t('market-shop-dashboard:product.itemAdded', { summary: values.summary }),
        )
        navigateToInventory()
        return
      }

      await updateMarketShopProduct({
        data: payload,
        id: itemId,
      })
      toastFactory.success(
        t('market-shop-dashboard:product.itemUpdated', { summary: values.summary }),
      )
      navigateToInventory()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const save = useFormSave({
    form,
    isEdit: isEditMode,
    onSubmit: handleSubmit,
    onUnchanged: navigateToInventory,
  })

  const categoriesOptions: SelectOption<number | string>[] = [
    {
      label: t('market-shop-dashboard:product.category.optionsAllLabel'),
      value: 'all',
    },
    ...(settings?.categories?.map((category) => ({
      label: category.name,
      value: category.id,
    })) ?? []),
  ]

  if (isLoading) {
    return <MarketShopProductSkeleton />
  }

  return (
    <FormProvider {...form}>
      <S.PageCard>
        <S.FormLayout>
          <S.FieldsColumn>
            <S.FieldRow>
              <TextInputFormField
                label={t('market-shop-dashboard:product.summary.label')}
                name="summary"
                placeholder={t('market-shop-dashboard:product.summary.placeholder')}
                helperText={t('market-shop-dashboard:product.summary.hint', {
                  max: ITEM_NAME_MAX_LENGTH,
                })}
                disabled={isSubmitting}
                maxLength={ITEM_NAME_MAX_LENGTH}
                autoFocus={!isEditMode}
              />
              <SelectFormField
                options={categoriesOptions}
                type="single"
                name="categoryId"
                label={t('market-shop-dashboard:product.category.label')}
                placeholder={t('market-shop-dashboard:product.category.placeholder')}
                helperText={t('market-shop-dashboard:product.category.helperText')}
                disabled={isSubmitting}
              />
            </S.FieldRow>

            <TextAreaFormField
              label={t('market-shop-dashboard:product.description.label')}
              name="description"
              placeholder={t('market-shop-dashboard:product.description.placeholder')}
              maxLength={ITEM_DESCRIPTION_MAX_LENGTH}
              rows={5}
              helperText={t('market-shop-dashboard:product.description.hint', {
                max: ITEM_DESCRIPTION_MAX_LENGTH,
              })}
              disabled={isSubmitting}
            />

            <SelectFormField
              type="multi"
              name="tags"
              label={t('market-shop-dashboard:product.tags.label')}
              options={tags.map((tag) => ({
                badgeColor: tag.name === 'NEW' ? 'green' : 'orange',
                label:
                  tag.name === 'NEW'
                    ? t('market-shop-dashboard:product.tags.optionsNewLabel')
                    : tag.name,
                value: tag.id,
              }))}
              placeholder={t('market-shop-dashboard:product.tags.placeholder')}
              display="badges"
              helperText={t('market-shop-dashboard:product.tags.helperText')}
              disabled={isSubmitting}
            />

            {isDiscountTagAdded && (
              <NumericInputFormField
                label={t('market-shop-dashboard:product.discount.label')}
                name="discount"
                placeholder="0"
                mode="decimal"
                decimalPlaces={2}
                min={0}
                max={99}
                helperText={t('market-shop-dashboard:product.discount.helperText')}
                disabled={isSubmitting}
              />
            )}

            <S.FieldRow>
              <NumericInputFormField
                disabled
                name="sku"
                label={t('market-shop-dashboard:product.sku.label')}
                placeholder={product?.sku ?? t('market-shop-dashboard:product.sku.placeholder')}
              />
              <NumericInputFormField
                label={t('market-shop-dashboard:product.price.label')}
                name="price"
                placeholder="0.00"
                mode="decimal"
                decimalPlaces={2}
                min={0}
                disabled={isSubmitting}
              />
            </S.FieldRow>

            <S.FieldRow>
              <S.InStockContainer>
                <NumericInputFormField
                  label={t('market-shop-dashboard:product.quantity.label')}
                  tooltip={t('market-shop-dashboard:product.quantity.tooltip')}
                  name="quantity"
                  placeholder="0"
                  mode="integer"
                  min={0}
                  disabled={formValues.unlimited || isSubmitting}
                />
                <Switch
                  label={t('market-shop-dashboard:product.unlimitedLabel')}
                  checked={formValues.unlimited}
                  onChange={(e) => {
                    setValue('unlimited', e.target.checked, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    })
                    if (e.target.checked || isSubmitted) void trigger('quantity')
                  }}
                />
              </S.InStockContainer>

              <NumericInputFormField
                label={t('market-shop-dashboard:product.tax.label')}
                name="tax"
                placeholder="0"
                mode="integer"
                min={0}
                max={100}
                helperText={t('market-shop-dashboard:product.tax.helperText')}
                disabled={isSubmitting}
              />
            </S.FieldRow>
          </S.FieldsColumn>

          <S.ImageColumn>
            <S.ImageSectionTitle>
              {t('market-shop-dashboard:product.image.title')}
            </S.ImageSectionTitle>
            <S.ImageUploadSection>
              <FileUploadFormField
                showBrowseLibrary
                mode="auto"
                initialSearch={formValues.summary ?? ''}
                name="image"
                acceptedTypes={PRODUCT_IMAGE_ACCEPTED_TYPES}
                maxFiles={1}
                {...IMAGE_UPLOAD_LIMITS}
                imageOptimization={PHOTO_IMAGE_OPTIMIZATION}
                multiple={false}
                triggerMode="dropzone"
                layout="minimized"
                disabled={isSubmitting}
                onUpload={async ([file]) => {
                  const result = await uploadProductImage({ file })
                  return [getMarketShopImageHref(result.image)]
                }}
                onValidationError={handleImageValidationError}
              />
              <S.ImageHelperText>
                {t('market-shop-dashboard:product.image.helperTextLine1')}
                <br />
                {t('market-shop-dashboard:product.image.helperTextLine2')}
              </S.ImageHelperText>
            </S.ImageUploadSection>
          </S.ImageColumn>
        </S.FormLayout>

        <S.Footer>
          <Button use="secondary" disabled={isSubmitting} onClick={handleCancel}>
            {t('common:actions.cancel')}
          </Button>
          <Button loading={isSubmitting} onClick={save}>
            {isEditMode
              ? t('common:actions.saveChanges')
              : t('market-shop-dashboard:product.addButton')}
            <Icon icon={isEditMode ? CheckIcon : PlusIcon} />
          </Button>
        </S.Footer>
      </S.PageCard>
    </FormProvider>
  )
}
